import { useSyncExternalStore, useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Alert, Badge, Button, InlineLoader, Panel } from '../../components/ui'
import { ApiError } from '../../api/client'
import { cataloguePosts, skipPost } from '../../api/catalogue'
import type { CataloguePost, PostState } from '../../api/catalogue'
import { snapshot, startSync, stopSync, subscribe } from './syncRun'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'
import { PostCard } from './PostCard'

const PAGE = 20

/**
 * The queue between the agency's channel and the inventory.
 *
 * One screen and two decisions per advert: is this an object, and is what the
 * model read correct. Everything else about the object -- the price, the rooms,
 * whether it may be offered -- is edited on the object itself, where it already
 * works. Duplicating any of that here would be a second place to keep right.
 *
 * The tabs are the post's own state, not a filter somebody composed. Adverts
 * waiting for a decision are what the screen is for, so they open first; the
 * others exist so that "where did that flat go" has an answer.
 */
export function CatalogueScreen() {
  const [state, setState] = useState<PostState>('parsed')
  const [limit, setLimit] = useState(PAGE)
  const { can } = useSession()
  // The run belongs to the module, not to this component: leaving the screen
  // must not orphan it, and coming back must show what is really happening.
  const sync = useSyncExternalStore(subscribe, snapshot)
  const running = sync.running
  const synced = sync.result
  const client = useQueryClient()

  const posts = useQuery({
    queryKey: ['catalogue', state, limit],
    queryFn: () => cataloguePosts({ state, limit }),
    placeholderData: keepPreviousData,
  })

  // The channel is not configured in this deployment. Saying so is better than
  // an empty queue, which reads as "the channel holds nothing".
  const notConfigured = posts.error instanceof ApiError && posts.error.status === 501

  return (
    <div className="catalogue">
      <header className="catalogue__header">
        <div>
          <h1 className="page-header__title">{t('catalogue.title')}</h1>
          {posts.data ? (
            <p className="catalogue__subtitle">
              {t('catalogue.subtitle', { channel: posts.data.channel })}
            </p>
          ) : null}
        </div>
        {can('listings.write') && !notConfigured ? (
          <div className="catalogue__syncControls">
            <Button
              onClick={() => {
                startSync(client)
              }}
              disabled={running}
            >
              {running ? t('catalogue.syncing') : t('catalogue.sync')}
            </Button>
            {running ? (
              <Button
                variant="secondary"
                onClick={() => {
                  stopSync()
                }}
              >
                {t('catalogue.syncStop')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>

      {/*
        The one thing somebody has to know before pressing anything: an imported
        object is not an offer yet, and a blank field is not a zero.
      */}
      <Alert tone="info">{t('catalogue.explain')}</Alert>

      {sync.failure !== null ? (
        <Alert tone={sync.read + sync.covers > 0 ? 'warning' : 'error'}>{sync.failure}</Alert>
      ) : running ? (
        <Alert tone="info">
          <span className="catalogue__progress">
            <span className="spinner" aria-hidden="true" />
            {sync.left === undefined
              ? t('catalogue.syncStarting')
              : t('catalogue.syncProgress', {
                  read: sync.read,
                  covers: sync.covers,
                  left: sync.left,
                })}
          </span>
        </Alert>
      ) : null}

      {synced === null ? null : (
        <Alert tone="info">
          {synced.new > 0
            ? t('catalogue.syncDone', {
                new: synced.new,
                read: synced.read,
                covers: synced.covers,
              })
            : t('catalogue.syncNothingNew')}
          {/*
            Closing an object is the half of a sync somebody has to be told
            about: it takes a flat off the market, and a silent withdrawal is
            what makes a manager stop trusting the screen.
          */}
          {synced.closed > 0 ? ` ${t('catalogue.syncClosed', { closed: synced.closed })}` : ''}
          {synced.handed > 0 ? ` ${t('catalogue.syncHanded', { handed: synced.handed })}` : ''}
          {synced.failed !== '' ? ` ${t('catalogue.syncPartial', { step: synced.failed })}` : ''}
        </Alert>
      )}

      {notConfigured ? (
        <Alert tone="warning">{t('catalogue.notConfigured')}</Alert>
      ) : (
        <>
          <nav className="catalogue__tabs" aria-label={t('catalogue.title')}>
            {(['parsed', 'pending', 'imported', 'let', 'announcement', 'skipped'] as const).map(
              (each) => (
                <button
                  key={each}
                  type="button"
                  className={`catalogue__tab${each === state ? ' catalogue__tab--on' : ''}`}
                  onClick={() => {
                    setState(each)
                    setLimit(PAGE)
                  }}
                >
                  {t(`catalogue.states.${each}` as 'catalogue.states.parsed')}
                  {/*
                    The count belongs on the tab. A queue showing "Ждут решения"
                    with nothing behind it and 22 unread posts one tab away looks
                    finished, and the person stops pressing sync.
                  */}
                  {posts.data === undefined || posts.data.counts[each] === undefined ? null : (
                    <span className="catalogue__tabCount">{posts.data.counts[each]}</span>
                  )}
                </button>
              ),
            )}
          </nav>

          {posts.isError ? (
            <Alert tone="error">{t('catalogue.failed')}</Alert>
          ) : posts.isPending ? (
            <InlineLoader />
          ) : posts.data.posts.length === 0 ? (
            <Panel className="catalogue__empty">
              <p>{t('catalogue.empty')}</p>
              <p className="catalogue__hint">{t('catalogue.emptyHint')}</p>
            </Panel>
          ) : (
            <>
              <ul className="catalogue__list">
                {posts.data.posts.map((each) => (
                  <PostItem key={each.id} post={each} />
                ))}
              </ul>
              {posts.data.more ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setLimit((current) => current + PAGE)
                  }}
                >
                  {t('catalogue.more')}
                </Button>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  )
}

/**
 * One advert, with the two buttons that decide its fate.
 *
 * The mutations live here rather than in the screen so that pressing "create" on
 * one post leaves the other nineteen alone: a shared pending flag would grey out
 * the whole list while one import fetches its photographs.
 */
function PostItem({ post }: { readonly post: CataloguePost }) {
  const client = useQueryClient()
  const { can } = useSession()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const skip = useMutation({
    mutationFn: () => skipPost(post.id, reason),
    onSuccess: () => {
      setAsking(false)
      return client.invalidateQueries({ queryKey: ['catalogue'] })
    },
  })

  return (
    <li className="catalogue__item">
      <PostCard post={post} />

      <footer className="catalogue__actions">
        {post.listingID ? (
          <>
            <Badge tone="accent">{t('catalogue.created', { photos: post.photos })}</Badge>
            {post.propertyID ? (
              // A router link, not an anchor: an anchor reloads the whole panel,
              // losing the queue's page and scroll for a navigation that is meant
              // to be a step sideways.
              <Link className="button button--secondary" to={`/objects/${post.propertyID}`}>
                {t('catalogue.openObject')}
              </Link>
            ) : null}
          </>
        ) : can('listings.write') ? (
          <>
            {asking ? (
              <span className="catalogue__skip">
                <input
                  className="field__input"
                  value={reason}
                  placeholder={t('catalogue.skipReason')}
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                />
                <Button
                  variant="secondary"
                  onClick={() => {
                    skip.mutate()
                  }}
                  disabled={skip.isPending}
                >
                  {t('catalogue.skip')}
                </Button>
              </span>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setAsking(true)
                }}
              >
                {t('catalogue.skip')}
              </Button>
            )}
          </>
        ) : null}
      </footer>
    </li>
  )
}
